import { Link } from "react-router-dom";
import { Download, ArrowLeft } from "lucide-react";
import { LogoMark, WordMark, TAGLINE } from "@/components/Logo";
import usePageMeta from "@/hooks/usePageMeta";

/**
 * Renders a single brand card (dark or light).
 * Rendered as inline SVG so it can be exported/downloaded.
 */
const BrandCard = ({ variant, id }) => {
  const isDark = variant === "dark";
  const bg = isDark ? "#0A1220" : "#EFEBDF";
  const fg = isDark ? "#EFEBDF" : "#0A1220";
  const gold = "#C9A76A";

  const download = () => {
    // Build a self-contained SVG file matching the on-screen layout.
    const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 630" width="1200" height="630">
  <defs>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,500&amp;family=Outfit:wght@400;500&amp;display=swap');
      .word { font-family: 'Cormorant Garamond', serif; font-weight: 500; }
      .italic { font-style: italic; fill: ${gold}; }
      .motto { font-family: 'Cormorant Garamond', serif; font-style: italic; font-weight: 500; fill: ${gold}; }
      .label { font-family: 'Outfit', sans-serif; letter-spacing: 0.28em; font-size: 16px; fill: ${fg}; opacity: 0.55; }
      .rule { stroke: ${gold}; stroke-width: 1; }
    </style>
  </defs>
  <rect width="1200" height="630" fill="${bg}"/>
  <!-- monogram badge -->
  <rect x="490" y="130" width="90" height="90" fill="none" stroke="${fg}" stroke-width="1.5"/>
  <text x="535" y="196" text-anchor="middle" class="word italic" font-size="52" fill="${fg}">Ai</text>
  <line x1="513" y1="204" x2="557" y2="204" stroke="${gold}" stroke-width="1.5"/>
  <!-- wordmark -->
  <text x="600" y="330" text-anchor="middle" class="word" font-size="110" fill="${fg}">
    Always<tspan class="italic">Insured</tspan>
  </text>
  <!-- rule -->
  <line x1="490" y1="380" x2="710" y2="380" class="rule"/>
  <!-- motto -->
  <text x="600" y="440" text-anchor="middle" class="motto" font-size="42">
    Always there, in all ways possible.
  </text>
  <!-- footer label -->
  <text x="600" y="560" text-anchor="middle" class="label">
    LICENSED · AUTO · HOME · COMMERCIAL
  </text>
</svg>`;
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `alwaysinsured-logo-${variant}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-4">
      <div
        id={id}
        data-testid={`brand-card-${variant}`}
        className="aspect-[1200/630] w-full flex flex-col items-center justify-center relative overflow-hidden border"
        style={{
          backgroundColor: bg,
          color: fg,
          borderColor: isDark ? "rgba(239,235,223,0.15)" : "rgba(10,18,32,0.15)",
        }}
      >
        <div className="mb-8">
          <LogoMark size={72} className="!border-current" />
        </div>
        <div
          className="font-serif tracking-tight text-[6vw] md:text-[3.5rem] lg:text-[4.5rem] leading-none"
          style={{ color: fg }}
        >
          Always
          <span className="italic" style={{ color: gold }}>
            Insured
          </span>
        </div>
        <div className="mt-3 h-px w-40" style={{ backgroundColor: gold }} />
        <div
          className="mt-6 font-serif italic text-lg md:text-2xl lg:text-3xl tracking-tight"
          style={{ color: gold }}
        >
          {TAGLINE.replace(/·/g, ",")}
        </div>
        <div
          className="absolute bottom-8 left-0 right-0 text-center overline"
          style={{ color: fg, opacity: 0.55, letterSpacing: "0.28em" }}
        >
          Licensed · Auto · Home · Commercial
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="overline text-bone-100/60">{isDark ? "Dark version" : "Light version"}</div>
        <button
          data-testid={`download-logo-${variant}`}
          onClick={download}
          className="inline-flex items-center gap-2 border border-bone-100/25 hover:border-terracotta-500 hover:text-terracotta-500 transition-colors px-4 py-2 text-sm text-bone-100"
        >
          <Download size={14} /> Download SVG
        </button>
      </div>
    </div>
  );
};

export default function LogoPage() {
  usePageMeta({
    title: "Brand Assets — AlwaysInsured",
    description: "Download the AlwaysInsured logo and brand assets in dark and light versions.",
  });
  return (
    <div className="min-h-screen bg-forest-900 text-bone-100 px-6 md:px-12 lg:px-16 py-12">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-16">
          <Link
            to="/"
            data-testid="logo-back-home"
            className="inline-flex items-center gap-2 text-bone-100/70 hover:text-bone-100"
          >
            <ArrowLeft size={16} /> Home
          </Link>
          <div className="overline text-bone-100/50">Brand · Assets</div>
        </div>

        <div className="max-w-3xl mb-16">
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-bone-100 leading-[0.95] tracking-tight">
            The logo <span className="italic text-terracotta-400">& the motto.</span>
          </h1>
          <div className="overline text-terracotta-500 mt-4">Brand mark</div>
          <p className="mt-6 text-bone-100/70 max-w-xl leading-relaxed">
            Two versions — for dark surfaces and for light. Right-click any card to save the image,
            or use <strong className="text-bone-100">Download SVG</strong> for a print-ready vector
            that scales to any size without losing quality.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <BrandCard variant="dark" id="brand-dark" />
          <BrandCard variant="light" id="brand-light" />
        </div>

        <div className="mt-16 border-t border-bone-100/10 pt-8 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
          <div>
            <div className="font-serif text-2xl text-bone-100">AlwaysInsured</div>
            <div className="overline text-bone-100/50 mt-2">Wordmark</div>
          </div>
          <div>
            <div className="font-serif italic text-2xl text-terracotta-400">
              Always there, in all ways possible.
            </div>
            <div className="overline text-bone-100/50 mt-2">Motto</div>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 border border-bone-100/20" style={{ background: "#0A1220" }} title="#0A1220" />
              <span className="w-8 h-8 border border-bone-100/20" style={{ background: "#EFEBDF" }} title="#EFEBDF" />
              <span className="w-8 h-8 border border-bone-100/20" style={{ background: "#C9A76A" }} title="#C9A76A" />
            </div>
            <div className="overline text-bone-100/50 mt-2">Palette</div>
          </div>
        </div>
      </div>
    </div>
  );
}
